import AcademiaSelect from '../common/AcademiaSelect';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import {
  JOURS_SEMAINE,
  resolveFormateurDisponibiliteCreneauOptions,
  type FormateurDiplome,
  type FormateurDisponibilite,
  type FormateurReferencement,
} from '../../types/formateurProfilExtras';

type Props = {
  diplomes: FormateurDiplome[];
  disponibilites: FormateurDisponibilite[];
  referencements: FormateurReferencement[];
  onDiplomesChange: (rows: FormateurDiplome[]) => void;
  onDisponibilitesChange: (rows: FormateurDisponibilite[]) => void;
  onReferencementsChange: (rows: FormateurReferencement[]) => void;
  readOnly?: boolean;
};

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs font-black uppercase tracking-wider text-on-surface-variant">{children}</h3>
  );
}

export default function FormateurProfilExtrasEditor({
  diplomes,
  disponibilites,
  referencements,
  onDiplomesChange,
  onDisponibilitesChange,
  onReferencementsChange,
  readOnly = false,
}: Props) {
  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <SectionTitle>Diplômes &amp; certifications</SectionTitle>
        {diplomes.length === 0 ? (
          <p className="text-sm text-on-surface-variant">Aucun diplôme renseigné.</p>
        ) : (
          <ul className="space-y-2">
            {diplomes.map((d, i) => (
              <li
                key={`dip-${i}`}
                className="grid gap-2 rounded-xl border border-outline-variant/20 bg-surface-container-low p-3 sm:grid-cols-3"
              >
                <input
                  className="academia-field w-full"
                  placeholder="Intitulé *"
                  value={d.libelle}
                  disabled={readOnly}
                  onChange={(e) => {
                    const next = [...diplomes];
                    next[i] = { ...d, libelle: e.target.value };
                    onDiplomesChange(next);
                  }}
                />
                <input
                  className="academia-field w-full"
                  placeholder="Établissement"
                  value={d.etablissement ?? ''}
                  disabled={readOnly}
                  onChange={(e) => {
                    const next = [...diplomes];
                    next[i] = { ...d, etablissement: e.target.value };
                    onDiplomesChange(next);
                  }}
                />
                <div className="flex gap-2">
                  <input
                    className="academia-field min-w-0 flex-1"
                    placeholder="Année"
                    value={d.annee ?? ''}
                    disabled={readOnly}
                    onChange={(e) => {
                      const next = [...diplomes];
                      next[i] = { ...d, annee: e.target.value };
                      onDiplomesChange(next);
                    }}
                  />
                  {!readOnly ? (
                    <button
                      type="button"
                      className="rounded-lg border border-outline-variant/30 px-2 text-on-surface-variant hover:bg-surface-container-high"
                      onClick={() => onDiplomesChange(diplomes.filter((_, j) => j !== i))}
                      aria-label="Supprimer"
                    >
                      <LucideSymbolIcon name="delete_forever" tone="inherit" size={18} />
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
        {!readOnly ? (
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg border border-dashed border-primary/40 px-3 py-2 text-xs font-bold text-primary"
            onClick={() => onDiplomesChange([...diplomes, { libelle: '' }])}
          >
            <LucideSymbolIcon name="add" tone="primary" size={16} />
            Ajouter un diplôme
          </button>
        ) : null}
      </div>

      <div className="space-y-3 border-t border-outline-variant/15 pt-6">
        <SectionTitle>Disponibilités</SectionTitle>
        {disponibilites.length === 0 ? (
          <p className="text-sm text-on-surface-variant">Aucune disponibilité renseignée.</p>
        ) : (
          <ul className="space-y-2">
            {disponibilites.map((d, i) => (
              <li
                key={`disp-${i}`}
                className="grid gap-2 rounded-xl border border-outline-variant/20 bg-surface-container-low p-3 sm:grid-cols-3"
              >
                <AcademiaSelect
                  className="w-full"
                  value={d.jour}
                  disabled={readOnly}
                  onChange={(e) => {
                    const next = [...disponibilites];
                    next[i] = { ...d, jour: e.target.value };
                    onDisponibilitesChange(next);
                  }}
                >
                  <option value="">Jour</option>
                  {JOURS_SEMAINE.map((j) => (
                    <option key={j} value={j}>
                      {j}
                    </option>
                  ))}
                </AcademiaSelect>
                <AcademiaSelect
                  className="w-full"
                  value={d.creneau ?? ''}
                  disabled={readOnly}
                  onChange={(e) => {
                    const next = [...disponibilites];
                    next[i] = { ...d, creneau: e.target.value };
                    onDisponibilitesChange(next);
                  }}
                  aria-label="Créneau"
                >
                  <option value="">Créneau</option>
                  {resolveFormateurDisponibiliteCreneauOptions(d.creneau).map((creneau) => (
                    <option key={creneau} value={creneau}>
                      {creneau}
                    </option>
                  ))}
                </AcademiaSelect>
                <div className="flex gap-2 sm:col-span-1">
                  <input
                    className="academia-field min-w-0 flex-1"
                    placeholder="Commentaire"
                    value={d.commentaire ?? ''}
                    disabled={readOnly}
                    onChange={(e) => {
                      const next = [...disponibilites];
                      next[i] = { ...d, commentaire: e.target.value };
                      onDisponibilitesChange(next);
                    }}
                  />
                  {!readOnly ? (
                    <button
                      type="button"
                      className="rounded-lg border border-outline-variant/30 px-2"
                      onClick={() => onDisponibilitesChange(disponibilites.filter((_, j) => j !== i))}
                      aria-label="Supprimer"
                    >
                      <LucideSymbolIcon name="delete_forever" tone="inherit" size={18} />
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
        {!readOnly ? (
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg border border-dashed border-primary/40 px-3 py-2 text-xs font-bold text-primary"
            onClick={() => onDisponibilitesChange([...disponibilites, { jour: 'LUNDI' }])}
          >
            <LucideSymbolIcon name="add" tone="primary" size={16} />
            Ajouter une disponibilité
          </button>
        ) : null}
      </div>

      <div className="space-y-3 border-t border-outline-variant/15 pt-6">
        <SectionTitle>Référencements</SectionTitle>
        {referencements.length === 0 ? (
          <p className="text-sm text-on-surface-variant">Aucune référence renseignée.</p>
        ) : (
          <ul className="space-y-2">
            {referencements.map((r, i) => (
              <li
                key={`ref-${i}`}
                className="grid gap-2 rounded-xl border border-outline-variant/20 bg-surface-container-low p-3 sm:grid-cols-2"
              >
                <input
                  className="academia-field w-full"
                  placeholder="Nom *"
                  value={r.nom}
                  disabled={readOnly}
                  onChange={(e) => {
                    const next = [...referencements];
                    next[i] = { ...r, nom: e.target.value };
                    onReferencementsChange(next);
                  }}
                />
                <input
                  className="academia-field w-full"
                  placeholder="Fonction"
                  value={r.fonction ?? ''}
                  disabled={readOnly}
                  onChange={(e) => {
                    const next = [...referencements];
                    next[i] = { ...r, fonction: e.target.value };
                    onReferencementsChange(next);
                  }}
                />
                <input
                  className="academia-field w-full"
                  placeholder="Téléphone"
                  value={r.telephone ?? ''}
                  disabled={readOnly}
                  onChange={(e) => {
                    const next = [...referencements];
                    next[i] = { ...r, telephone: e.target.value };
                    onReferencementsChange(next);
                  }}
                />
                <div className="flex gap-2">
                  <input
                    className="academia-field min-w-0 flex-1"
                    placeholder="E-mail"
                    value={r.email ?? ''}
                    disabled={readOnly}
                    onChange={(e) => {
                      const next = [...referencements];
                      next[i] = { ...r, email: e.target.value };
                      onReferencementsChange(next);
                    }}
                  />
                  {!readOnly ? (
                    <button
                      type="button"
                      className="rounded-lg border border-outline-variant/30 px-2"
                      onClick={() => onReferencementsChange(referencements.filter((_, j) => j !== i))}
                      aria-label="Supprimer"
                    >
                      <LucideSymbolIcon name="delete_forever" tone="inherit" size={18} />
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
        {!readOnly ? (
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg border border-dashed border-primary/40 px-3 py-2 text-xs font-bold text-primary"
            onClick={() => onReferencementsChange([...referencements, { nom: '' }])}
          >
            <LucideSymbolIcon name="add" tone="primary" size={16} />
            Ajouter une référence
          </button>
        ) : null}
      </div>
    </div>
  );
}
